/**
 * Profile photo upload to POST /api/upload/photo (multipart).
 * Returns structured results for moderation failures; throws only on cancel/timeout/abort.
 */

const API_BASE = "/api";

export const PHOTO_UPLOAD_PER_FILE_TIMEOUT_MS = 90000;

function httpErrorMessage(status) {
  if (status === 413) {
    return "This file is too large for the server. Try a smaller image or contact support.";
  }
  if (status === 401) return "Please sign in again.";
  if (status === 403) return "You don't have permission to upload this photo.";
  if (status === 429) return "Too many uploads. Please wait a moment and try again.";
  if (status === 502 || status === 504) {
    return "The service is temporarily unavailable. Please try again shortly.";
  }
  if (status >= 500) {
    return "The server had a problem. Please try again in a moment.";
  }
  return "Something went wrong uploading this photo. Please try again.";
}

/**
 * Upload one photo. Does not throw for HTTP error bodies (e.g. moderation rejection);
 * returns { ok, url?, error? }. Throws only on AbortError from `signal`.
 */
export async function uploadPhotoFile(file, { signal } = {}) {
  const fd = new FormData();
  fd.append("photo", file);
  let response;
  try {
    response = await fetch(`${API_BASE}/upload/photo`, {
      method: "POST",
      credentials: "include",
      body: fd,
      signal,
    });
  } catch (e) {
    if (e.name === "AbortError") throw e;
    return {
      ok: false,
      error: "Network error. Check your connection and try again.",
    };
  }

  const text = await response.text();
  let parsed = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (response.ok && parsed && parsed.success && parsed.url) {
    return { ok: true, url: parsed.url };
  }

  const msg =
    parsed && typeof parsed.error === "string"
      ? parsed.error
      : httpErrorMessage(response.status);

  return { ok: false, error: msg };
}

/**
 * Same as uploadPhotoFile but enforces per-file timeout and optional batch cancel signal.
 * Throws Error with code 'CANCELLED' or 'TIMEOUT'.
 */
export async function uploadPhotoFileWithGuards(file, { batchSignal } = {}) {
  const controller = new AbortController();
  let userCancelled = false;

  if (batchSignal) {
    if (batchSignal.aborted) {
      const err = new Error("Upload cancelled.");
      err.code = "CANCELLED";
      throw err;
    }
    batchSignal.addEventListener(
      "abort",
      () => {
        userCancelled = true;
        controller.abort();
      },
      { once: true }
    );
  }

  const tid = setTimeout(() => {
    controller.abort();
  }, PHOTO_UPLOAD_PER_FILE_TIMEOUT_MS);

  try {
    const result = await uploadPhotoFile(file, { signal: controller.signal });
    clearTimeout(tid);
    return result;
  } catch (e) {
    clearTimeout(tid);
    if (e.name === "AbortError") {
      if (userCancelled || batchSignal?.aborted) {
        const err = new Error("Upload cancelled.");
        err.code = "CANCELLED";
        throw err;
      }
      const err = new Error(
        "This photo took too long to upload. Check your connection and try again."
      );
      err.code = "TIMEOUT";
      throw err;
    }
    throw e;
  }
}
